// App.js1
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ProductAnalytics from './pages/analytics/ProductAnalytics';
import PortalWiseSalesTrend from './pages/analytics/PortalWiseSalesTrend';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Orders from './pages/orders/Orders';
import OrderDetail from './pages/orders/OrderDetail';
import FixOrdersEntries from './pages/orders/FixOrdersEntries';
import ZoneWiseSalesTrend from './pages/analytics/ZoneWiseSalesTrend';
import StateWiseSalesTrend from './pages/analytics/StateWiseSalesTrend';
import Profile from './pages/Profile';
import { useEffect } from 'react';
import ZoneDetailSalesTrend from './pages/analytics/ZoneDetailSalesTrend';
import ProductDetail from "./pages/analytics/ProductDetail";
import PollingStatus from './pages/analytics/PollingStatus';
import ManualPolling from './pages/analytics/ManualPolling';
import ManualPollByDateRange from './pages/analytics/ManualPollByDateRange';
import ManualPollHistoryDetail from './pages/analytics/ManualPollHistoryDetail';
import CronLogs from './pages/analytics/CronLogs';
import SalesComparison from './pages/analytics/SalesComparison';
import HourlySales from './pages/analytics/HourlySales';
import ShopifyCampaignAnalytics from './pages/analytics/ShopifyCampaignAnalytics';
import AdminManagement from './pages/admin/AdminManagement';
import Platform from './pages/Platform';
import ProductManagement from './pages/products/ProductManagement';
import CommonProductDetail from './pages/products/CommonProductDetail';
import Settings from './pages/Settings';
import Backup from './pages/Backup';

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            style={{ zIndex: 99999 }}
          />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Dashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/product"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProductAnalytics />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/portal-wise-sales-trend"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PortalWiseSalesTrend />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/zone-wise-sales-trend"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ZoneWiseSalesTrend />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/state-wise-sales-trend"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <StateWiseSalesTrend />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/product-by-state"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProductAnalytics />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/zone-detail-sales-trend"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ZoneDetailSalesTrend />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/product-detail/:productName"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProductDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/polling-status"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PollingStatus />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/manual-polling"
              element={
                <AdminRoute>
                  <DashboardLayout>
                    <ManualPolling />
                  </DashboardLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/analytics/manual-poll-by-date"
              element={
                <AdminRoute>
                  <DashboardLayout>
                    <ManualPollByDateRange />
                  </DashboardLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/analytics/manual-poll-history/:id"
              element={
                <AdminRoute>
                  <DashboardLayout>
                    <ManualPollHistoryDetail />
                  </DashboardLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/analytics/cron-logs"
              element={
                <AdminRoute>
                  <DashboardLayout>
                    <CronLogs />
                  </DashboardLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/analytics/sales-comparison"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <SalesComparison />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/hourly-sales"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <HourlySales />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/shopify-campaign"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ShopifyCampaignAnalytics />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Orders />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/detail/:orderId"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <OrderDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/fix-entries"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <FixOrdersEntries />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Profile />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Platform />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AdminManagement />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/backup"
              element={
                <AdminRoute>
                  <DashboardLayout>
                    <Backup />
                  </DashboardLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <AdminRoute>
                  <DashboardLayout>
                    <Settings />
                  </DashboardLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProductManagement />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/common/:id"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CommonProductDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AuthProvider>
      </QueryClientProvider>
    </Router>
  );
}

export default App;
